import React, { useEffect, useState } from "react";
import { deleteDocumentRecord, getAllDocumentList, uploadDocument, downloadDocument } from "../../util/APIUtils";
import { LoadingOverlay } from "../../util/Helpers";
import { Button, Card, CardBody, CardHeader, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormText } from "reactstrap";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import 'react-bootstrap-table/dist/react-bootstrap-table-all.min.css';
import { ButtonStyles } from "../../constants/ButtonStyles";


const options = {
    sortIndicator: true,
    paginationSize: 3,
    clearSearch: true,
    alwaysShowAllBtns: true,
    withFirstAndLast: true,
    hideSizePerPage: false,
    hidePageListOnlyOnePage: false,
};

const DocumentCenter = () => {
    const [data, setData] = useState([]);
    const [block, setBlock] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const [uniqueId, setUniqueId] = useState('DOC-' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '-001');
    const [documentName, setDocumentName] = useState('');
    const [expiredDate, setExpiredDate] = useState('');
    const [description, setDescription] = useState('');
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        loadReqData();
    }, []);

    const loadReqData = () => {
        setBlock(true);
        getAllDocumentList().then((response) => {
            setData(response);
            setBlock(false);
        });
    };

    const saveDocument = (formData) => {
        setBlock(true);
        return uploadDocument(formData)
            .then((response) => {
                swal('Document uploaded successfully.');
                // Refresh list and clear form fields
                loadReqData();
                setShowModal(false);
                setUniqueId('DOC-' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '-001');
                setDocumentName('');
                setExpiredDate('');
                setDescription('');
                setFiles([]);
                setErrors({});
                return response;
            })
            .catch((err) => {
                const message =
                    (err && err.message) ||
                    (err && err.response && err.response.data && err.response.data.message) ||
                    'Failed to upload document';
                swal('Upload error: ' + message);
                throw err;
            })
            .finally(() => {
                setBlock(false);
            });
    };

    const deleteDocument = (documentId) => {
        setBlock(true);
        deleteDocumentRecord(documentId)
            .then(() => {
                swal('Document deleted successfully.');
                loadReqData();
            })
            .catch((err) => {
                const message =
                    (err && err.message) ||
                    (err && err.response && err.response.data && err.response.data.message) ||
                    'Failed to delete document';
                swal('Delete error: ' + message);
            })
            .finally(() => {
                setBlock(false);
            });
    }

    const renderActionButtons = (cell, row, rowIndex) => (
        <span>
            <Button
                className="btn btn-danger"
                id={`delete-${rowIndex}`}
                onClick={() => {
                    swal({
                        title: "Are you sure?",
                        text: "Once deleted, you will not be able to recover this document!",
                        icon: "warning",
                        buttons: true,
                        dangerMode: true,
                    }).then((willDelete) => {
                        if (willDelete) {
                            deleteDocument(row.id);
                        }
                    });
                }}
            >
                <i className="fa fa-minus-circle" />
            </Button>
        </span>
    );

    // Render file links with icons instead of full path text
    const getIconForFilename = (name) => {
        const ext = (name || '').split('.').pop().toLowerCase();
        if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg'].includes(ext)) return 'fa-file-image-o';
        if (['pdf'].includes(ext)) return 'fa-file-pdf-o';
        if (['doc', 'docx', 'odt'].includes(ext)) return 'fa-file-word-o';
        if (['xls', 'xlsx', 'csv'].includes(ext)) return 'fa-file-excel-o';
        if (['zip', 'rar', '7z'].includes(ext)) return 'fa-file-archive-o';
        if (['txt', 'md'].includes(ext)) return 'fa-file-text-o';
        return 'fa-file-o';
    };

    const renderFiles = (cell, row) => {
        if (!cell) return '';
        // cell may be comma separated paths
        const parts = String(cell).split(',').map(p => p.trim()).filter(Boolean);
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {parts.map((p, idx) => {
                    // normalize slashes
                    let url = p.replace(/\\\\/g, '/').replace(/\\/g, '/');
                    if (!/^https?:\/\//i.test(url) && url.charAt(0) !== '/') {
                        url = '/' + url; // make it a root-relative URL
                    }
                    const name = url.split('/').pop();
                    const icon = getIconForFilename(name);
                    return (
                        <a key={idx} href="#" onClick={e => { e.preventDefault(); downloadDocumentFile(row.id, name); }} style={{ color: '#333', textDecoration: 'none', cursor: 'pointer' }}>
                            <i className={`fa ${icon} mr-2`} aria-hidden="true" />
                            <span style={{ marginLeft: 6 }}>{name}</span>
                        </a>
                    );
                })}
            </div>
        );
    };
    const downloadDocumentFile = async (documentId, fileName) => {
        try {
            // Fetch file via API helper and open as blob (downloadDocument handles auth)
            const resp = await downloadDocument(documentId, fileName);
            // APIUtils.request returns a Response object when non-JSON; ensure it's a Response
            const blob = await resp.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            // Try to extract filename from Content-Disposition header (RFC6266 / RFC5987)
            let downloadName = fileName;
            try {
                const cd = resp.headers.get('content-disposition') || resp.headers.get('Content-Disposition');
                if (cd) {
                    // filename*=UTF-8''encoded-name or filename="name"
                    let m = cd.match(/filename\*=UTF-8''([^;\n\r]+)/i);
                    if (m && m[1]) {
                        downloadName = decodeURIComponent(m[1].replace(/['"]+/g, ''));
                    } else {
                        m = cd.match(/filename=(?:\")?([^;\n\r\"]+)(?:\")?/i);
                        if (m && m[1]) {
                            downloadName = m[1].replace(/['"]+/g, '');
                        }
                    }
                }
            } catch (ex) {
                // ignore parsing errors and fall back to provided fileName
            }

            // Force download using the resolved filename so extension is preserved
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = downloadName || fileName || 'download';
            document.body.appendChild(a);
            a.click();
            a.remove();
            // revoke object URL after some time
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
        } catch (err) {
            swal('Error opening file: ' + (err.message || err));
        }
    };

    return <div>
        {block && <LoadingOverlay />}
        <Card style={{
            borderRadius: "15px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
            border: "none",
            overflow: "hidden"
        }}>
            <CardHeader style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                padding: "20px 25px",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: "none"
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <i className="fa fa-folder-open" style={{ fontSize: "24px", color: "#fff" }}></i>
                    <strong style={{ fontSize: '1.2rem', color: "#fff" }}>Document Center</strong>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <Button
                        style={ButtonStyles.successSm}
                        onClick={() => setShowModal(true)}
                    >
                        <i className="fa fa-plus" style={{ marginRight: "5px" }} /> Add Document
                    </Button>
                    <Button
                        style={ButtonStyles.secondarySm}
                        onClick={loadReqData}
                    >
                        <i className="fa fa-refresh" style={{ marginRight: "5px" }} /> Refresh
                    </Button>
                </div>
            </CardHeader>
            <CardBody style={{ padding: "25px" }}>

                {/* Document Upload Modal */}

                <Modal isOpen={showModal} toggle={() => setShowModal(!showModal)} centered modalClassName="doc-modal">
                    <ModalHeader toggle={() => setShowModal(false)}>Add Document</ModalHeader>
                    <ModalBody>
                        <Form>
                            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                                <FormGroup style={{ flex: '1 1 220px' }}>
                                    <Label for="uniqueId">Unique ID</Label>
                                    <Input id="uniqueId" value={uniqueId} onChange={e => setUniqueId(e.target.value)} />
                                    {errors.uniqueId && <FormText color="danger">{errors.uniqueId}</FormText>}
                                </FormGroup>
                                <FormGroup style={{ flex: '1 1 220px' }}>
                                    <Label for="documentName">Document Name</Label>
                                    <Input id="documentName" value={documentName} onChange={e => setDocumentName(e.target.value)} />
                                    {errors.documentName && <FormText color="danger">{errors.documentName}</FormText>}
                                </FormGroup>
                                <FormGroup style={{ flex: '1 1 220px' }}>
                                    <Label for="expiredDate">Expire Date</Label>
                                    <Input id="expiredDate" type="date" value={expiredDate} onChange={e => setExpiredDate(e.target.value)} />
                                    {errors.expiredDate && <FormText color="danger">{errors.expiredDate}</FormText>}
                                </FormGroup>
                            </div>

                            <FormGroup>
                                <Label for="description">Description</Label>
                                <Input id="description" type="textarea" value={description} onChange={e => setDescription(e.target.value)} />
                                {errors.description && <FormText color="danger">{errors.description}</FormText>}
                            </FormGroup>

                            <FormGroup>
                                <Label for="files">Files (select 1 or more)</Label>
                                <Input id="files" type="file" multiple onChange={e => setFiles(Array.from(e.target.files))} />
                                {errors.files && <FormText color="danger">{errors.files}</FormText>}
                                <FormText color="muted">Files will be appended to FormData under key `files` (multiple entries).</FormText>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                        <Button type="button" color="primary" onClick={() => {
                            // validate
                            const e = {};
                            if (!uniqueId) e.uniqueId = 'Unique ID is required';
                            if (!documentName) e.documentName = 'Document Name is required';
                            if (!expiredDate) e.expiredDate = 'Expire Date is required';
                            if (!description) e.description = 'Description is required';
                            if (!files || files.length === 0) e.files = 'At least one file is required';
                            setErrors(e);
                            if (Object.keys(e).length > 0) return;

                            // build payload
                            const documentObj = {
                                uniqueId,
                                documentName,
                                expiredDate,
                                description
                            };
                            const formData = new FormData();
                            formData.append('document', JSON.stringify(documentObj));
                            files.forEach(f => formData.append('files', f));

                            saveDocument(formData);
                        }}>Save</Button>
                    </ModalFooter>
                </Modal>
                <style>{`
                    /* Modern centered modal */
                    .doc-modal .modal-dialog{
                        position: fixed;
                        top: 8%;
                        left: 20%; /* shift modal to the left */
                        transform: translateX(0);
                        max-width: 40%;
                        width: 40%;
                        margin: 0; /* controlled positioning */
                        pointer-events: auto;
                        border-radius: 12px;
                    }
                    .doc-modal .modal-content{
                        border-radius: 12px;
                        box-shadow: 0 12px 40px rgba(0,0,0,0.35);
                        display:flex; flex-direction:column; max-height:80vh; overflow: hidden;
                    }
                    .doc-modal .modal-body{ overflow:auto; padding: 1.25rem; }
                    .doc-modal .modal-header{ border-bottom: none; padding: 1rem 1.25rem; }
                    .doc-modal .modal-footer{ border-top: none; padding: 0.75rem 1.25rem; }
                    @media (max-width: 1200px) {
                        .doc-modal .modal-dialog{ max-width: 75%; width: 75%; left: 12.5%; }
                    }
                    @media (max-width: 768px) {
                        .doc-modal .modal-dialog{ position: relative; top: auto; left: auto; transform: none; max-width: 95%; width: 95%; margin: 1rem auto; }
                    }
                    /* dim background overlay slight tweak */
                    .modal-backdrop.show { opacity: 0.45; }
                `}</style>

                <BootstrapTable
                    data={data}
                    version="4"
                    condensed
                    striped
                    hover
                    pagination
                    search
                    options={options}
                    exportCSV
                    csvFileName="document_center.csv"
                    trClassName="align-middle"
                    tableStyle={{ tableLayout: "fixed" }}
                >
                    <TableHeaderColumn isKey dataField="id" hidden></TableHeaderColumn>
                    <TableHeaderColumn dataField="uniqueId">Unique ID</TableHeaderColumn>
                    <TableHeaderColumn dataField="documentName">Name</TableHeaderColumn>
                    <TableHeaderColumn dataField="expiredDate">Expire Date</TableHeaderColumn>
                    <TableHeaderColumn dataField="uploadPaths" dataFormat={renderFiles}>Files</TableHeaderColumn>
                    <TableHeaderColumn dataField="description">Description</TableHeaderColumn>
                    <TableHeaderColumn dataField="actions" dataFormat={renderActionButtons} export={false}>Actions</TableHeaderColumn>

                </BootstrapTable>
            </CardBody>
        </Card>
    </div>;
};
export default DocumentCenter;


// WEBPACK FOOTER //
// ./src/components/CustomComponents/DocumntCenter.js